<template>
  <dashboard-layout title="Feedbacks">
    <template #actions>
      <button
        v-if="selectedFeedbacks.length > 0"
        @click="deleteSelectedFeedbacks"
        class="delete-btn"
        :disabled="deleting"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
        {{ deleting ? 'Deleting...' : `Delete (${selectedFeedbacks.length})` }}
      </button>
      <search-box v-model="searchQuery" placeholder="Search feedbacks…" />
      <button @click="exportFeedbacks" class="export-btn" :disabled="filteredFeedbacks.length === 0">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        Export ({{ filteredFeedbacks.length }})
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total feedbacks" :value="feedbacks.length.toLocaleString('en-US')" icon="messagecircle" accent />
      <stat-card label="Resolved" :value="resolvedCount.toLocaleString('en-US')" icon="clipboardcheck" />
      <stat-card label="Unresolved" :value="unresolvedCount.toLocaleString('en-US')" icon="inbox" />
      <stat-card label="Subscription cancels" :value="cancelCount.toLocaleString('en-US')" icon="userx" />
    </stat-grid>

    <data-panel flush>
      <template #title>Feedbacks <span class="muted">({{ filteredFeedbacks.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="resolveFilter" :options="resolveOptions" />
      </template>

      <loading-state v-if="loading" label="Loading feedbacks…" />

      <div v-else class="table-scroll-wrapper">
        <table class="feedbacks-table">
          <thead>
            <tr>
              <th class="checkbox-cell">
                <input
                  type="checkbox"
                  @change="toggleSelectAll"
                  :checked="selectedFeedbacks.length === filteredFeedbacks.length && filteredFeedbacks.length > 0"
                  class="checkbox"
                />
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>About</span>
                  <button @click.stop="toggleAboutDropdown" :class="['filter-icon-btn', { 'has-filter': selectedAboutFilters.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    <span v-if="selectedAboutFilters.length > 0" class="filter-badge">{{ selectedAboutFilters.length }}</span>
                  </button>
                  <div v-if="showAboutDropdown" class="filter-dropdown" @click.stop>
                    <input type="text" v-model="aboutFilterSearch" placeholder="Search..." class="filter-search-input">
                    <div class="filter-actions">
                      <button @click="selectAllAbout">Select All</button>
                      <button @click="clearAboutFilter">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="opt in filteredAboutOptions" :key="opt.value" :class="['filter-option', { 'filter-option-special': opt.special }]">
                        <input type="checkbox" :checked="selectedAboutFilters.includes(opt.value)" @change="toggleAboutFilter(opt.value)">
                        <span>{{ opt.label }}</span>
                      </label>
                      <div v-if="filteredAboutOptions.length === 0" class="filter-empty">No matches</div>
                    </div>
                  </div>
                </div>
              </th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Feedback</span>
                  <button @click.stop="toggleFeedbackDropdown" :class="['filter-icon-btn', { 'has-filter': selectedFeedbackFilters.length > 0 }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    <span v-if="selectedFeedbackFilters.length > 0" class="filter-badge">{{ selectedFeedbackFilters.length }}</span>
                  </button>
                  <div v-if="showFeedbackDropdown" class="filter-dropdown" @click.stop>
                    <input type="text" v-model="feedbackFilterSearch" placeholder="Search..." class="filter-search-input">
                    <div class="filter-actions">
                      <button @click="selectAllFeedback">Select All</button>
                      <button @click="clearFeedbackFilter">Clear</button>
                    </div>
                    <div class="filter-options">
                      <label v-for="value in filteredFeedbackOptions" :key="value" class="filter-option">
                        <input type="checkbox" :checked="selectedFeedbackFilters.includes(value)" @change="toggleFeedbackFilter(value)">
                        <span>{{ truncateText(value, 60) }}</span>
                      </label>
                      <div v-if="filteredFeedbackOptions.length === 0" class="filter-empty">No matches</div>
                    </div>
                  </div>
                </div>
              </th>
              <th>Cancel Reason</th>
              <th>Email</th>
              <th class="filterable-header">
                <div class="header-filter-container">
                  <span>Date</span>
                  <button @click.stop="toggleDateDropdown" :class="['filter-icon-btn', { 'has-filter': dateFrom || dateTo }]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    <span v-if="dateFrom || dateTo" class="filter-badge">•</span>
                  </button>
                  <div v-if="showDateDropdown" class="filter-dropdown filter-dropdown-date" @click.stop>
                    <div class="date-range-field">
                      <label>From</label>
                      <input type="date" v-model="dateFrom" @change="filterFeedbacks" class="date-input">
                    </div>
                    <div class="date-range-field">
                      <label>To</label>
                      <input type="date" v-model="dateTo" @change="filterFeedbacks" class="date-input">
                    </div>
                    <div class="filter-actions">
                      <button @click="clearDateFilter">Clear</button>
                    </div>
                  </div>
                </div>
              </th>
              <th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="feedback in filteredFeedbacks" :key="feedback.id"
                class="feedback-row">
              <td class="checkbox-cell" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedFeedbacks.includes(feedback.id)"
                  @change="toggleSelect(feedback.id)"
                  class="checkbox"
                />
              </td>
              <td @click="handleRowClick(feedback.id, $event)">
                {{ formatAboutLabel(feedback) }}
              </td>
              <td @click="handleRowClick(feedback.id, $event)" class="feedback-text">{{ truncateText(feedback.feedback, 100) }}</td>
              <td @click="handleRowClick(feedback.id, $event)">
                <span v-if="feedback.mainReason" class="cancel-reason-tag">{{ feedback.mainReason }}</span>
                <span v-else>-</span>
              </td>
              <td @click="handleRowClick(feedback.id, $event)">{{ feedback.email }}</td>
              <td @click="handleRowClick(feedback.id, $event)">{{ formatTimestamp(feedback.timestamp) }}</td>
              <td class="resolved-cell" @click.stop>
                <input
                  type="checkbox"
                  :checked="feedback.resolved"
                  @change="updateResolved(feedback.id, $event.target.checked)"
                  class="resolved-checkbox"
                />
              </td>
            </tr>
            <tr v-if="filteredFeedbacks.length === 0">
              <td colspan="7" class="no-data">No feedbacks found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </data-panel>

    <div v-if="showDeleteModal" class="delete-modal-overlay" @click="cancelDelete">
      <div class="delete-modal" @click.stop>
        <h2>Delete Feedbacks</h2>
        <p class="warning">You are about to delete {{ selectedFeedbacks.length }} feedback(s). This action cannot be undone.</p>
        <input type="password" v-model="deletePassword" placeholder="Enter admin password" autocomplete="new-password">
        <div class="modal-actions">
          <button @click="cancelDelete" class="cancel-btn">Cancel</button>
          <button @click="confirmDelete" class="delete-confirm-btn" :disabled="deleting">
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseFeedbackService } from '../services/firebaseFeedbackService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'

export default {
  name: 'FeedbackDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, FilterChips, LoadingState
  },
  setup() {
    const router = useRouter()
    const feedbackService = new FirebaseFeedbackService()

    const FILTER_STORAGE_KEY = 'feedbackDashboardFilters'
    const savedFilters = (() => {
      try {
        const raw = localStorage.getItem(FILTER_STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
      } catch (e) {
        return {}
      }
    })()

    const feedbacks = ref([])
    const filteredFeedbacks = ref([])
    const searchQuery = ref(savedFilters.searchQuery || '')
    const loading = ref(true)
    const resolveFilter = ref(savedFilters.resolveFilter || 'all')

    const SUBSCRIPTION_CANCEL = '__subscription_cancel__'
    const SUBSCRIPTION_CANCEL_OLD = '__subscription_cancel_old__'
    const selectedFeedbacks = ref([])
    const deleting = ref(false)
    const showDeleteModal = ref(false)
    const deletePassword = ref('')

    const selectedAboutFilters = ref(savedFilters.selectedAboutFilters || [])
    const selectedFeedbackFilters = ref(savedFilters.selectedFeedbackFilters || [])
    const showAboutDropdown = ref(false)
    const showFeedbackDropdown = ref(false)
    const showDateDropdown = ref(false)
    const dateFrom = ref(savedFilters.dateFrom || '')
    const dateTo = ref(savedFilters.dateTo || '')
    const aboutFilterSearch = ref('')
    const feedbackFilterSearch = ref('')

    const persistFilters = () => {
      try {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
          searchQuery: searchQuery.value,
          resolveFilter: resolveFilter.value,
          selectedAboutFilters: selectedAboutFilters.value,
          selectedFeedbackFilters: selectedFeedbackFilters.value,
          dateFrom: dateFrom.value,
          dateTo: dateTo.value
        }))
      } catch (e) {
        // ignore storage errors
      }
    }

    const resolvedCount = computed(() => feedbacks.value.filter(f => f.resolved).length)
    const unresolvedCount = computed(() => feedbacks.value.filter(f => !f.resolved).length)
    const cancelCount = computed(() => feedbacks.value.filter(f => f.subscriptionCancelFeedback).length)

    const resolveOptions = computed(() => [
      { value: 'all', label: 'All', count: feedbacks.value.length },
      { value: 'unresolved', label: 'Unresolved', count: unresolvedCount.value },
      { value: 'resolved', label: 'Resolved', count: resolvedCount.value }
    ])

    const uniqueAboutValues = computed(() => {
      return [...new Set(feedbacks.value.map(f => f.about).filter(Boolean))].sort()
    })

    const uniqueFeedbackValues = computed(() => {
      return [...new Set(feedbacks.value.map(f => f.feedback).filter(Boolean))].sort()
    })

    const aboutFilterOptions = computed(() => {
      const special = [
        { value: SUBSCRIPTION_CANCEL, label: 'Subscription Cancel', special: true },
        { value: SUBSCRIPTION_CANCEL_OLD, label: 'Subscription Cancel (Old)', special: true }
      ]
      const regular = uniqueAboutValues.value.map(v => ({ value: v, label: formatAbout(v), special: false }))
      return [...special, ...regular]
    })

    const filteredAboutOptions = computed(() => {
      const search = aboutFilterSearch.value.toLowerCase()
      if (!search) return aboutFilterOptions.value
      return aboutFilterOptions.value.filter(opt => opt.label.toLowerCase().includes(search))
    })

    const filteredFeedbackOptions = computed(() => {
      const search = feedbackFilterSearch.value.toLowerCase()
      if (!search) return uniqueFeedbackValues.value
      return uniqueFeedbackValues.value.filter(v => v.toLowerCase().includes(search))
    })

    const loadFeedbacks = async () => {
      try {
        const feedbacksData = await feedbackService.getAllFeedbacks()
        feedbacks.value = feedbacksData
        filterFeedbacks()
      } catch (error) {
        console.error('Error loading feedbacks:', error)
      } finally {
        loading.value = false
      }
    }

    const filterFeedbacks = () => {
      const query = searchQuery.value.toLowerCase()
      filteredFeedbacks.value = feedbacks.value.filter(feedback => {
        const matchesSearch = feedback.about?.toLowerCase().includes(query) ||
          feedback.email?.toLowerCase().includes(query) ||
          feedback.feedback?.toLowerCase().includes(query)

        let matchesResolveFilter = true
        if (resolveFilter.value === 'resolved') {
          matchesResolveFilter = feedback.resolved === true
        } else if (resolveFilter.value === 'unresolved') {
          matchesResolveFilter = !feedback.resolved
        }

        const matchesAboutFilter = selectedAboutFilters.value.length === 0 ||
          selectedAboutFilters.value.some(val => {
            if (val === SUBSCRIPTION_CANCEL) return feedback.subscriptionCancelFeedback === true
            if (val === SUBSCRIPTION_CANCEL_OLD) return feedback.about?.toLowerCase().includes('cancel')
            return feedback.about === val
          })

        const matchesFeedbackFilter = selectedFeedbackFilters.value.length === 0 ||
          selectedFeedbackFilters.value.includes(feedback.feedback)

        let matchesDateFilter = true
        if (dateFrom.value || dateTo.value) {
          const ts = feedback.timestamp
          let feedbackDate = null
          if (ts) {
            feedbackDate = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)
          }
          if (!feedbackDate || isNaN(feedbackDate.getTime())) {
            matchesDateFilter = false
          } else {
            if (dateFrom.value) {
              const from = new Date(dateFrom.value)
              from.setHours(0, 0, 0, 0)
              if (feedbackDate < from) matchesDateFilter = false
            }
            if (matchesDateFilter && dateTo.value) {
              const to = new Date(dateTo.value)
              to.setHours(23, 59, 59, 999)
              if (feedbackDate > to) matchesDateFilter = false
            }
          }
        }

        return matchesSearch && matchesResolveFilter && matchesAboutFilter && matchesFeedbackFilter && matchesDateFilter
      })
      persistFilters()
    }

    watch([searchQuery, resolveFilter], filterFeedbacks)

    const toggleDateDropdown = () => {
      showDateDropdown.value = !showDateDropdown.value
      showAboutDropdown.value = false
      showFeedbackDropdown.value = false
    }

    const clearDateFilter = () => {
      dateFrom.value = ''
      dateTo.value = ''
      filterFeedbacks()
    }

    const updateResolved = async (feedbackId, resolved) => {
      try {
        await feedbackService.updateResolved(feedbackId, resolved)
        const feedback = feedbacks.value.find(f => f.id === feedbackId)
        if (feedback) {
          feedback.resolved = resolved
        }
        filterFeedbacks()
      } catch (error) {
        console.error('Error updating resolved status:', error)
        alert('Failed to update resolved status')
      }
    }

    const toggleSelect = (feedbackId) => {
      const index = selectedFeedbacks.value.indexOf(feedbackId)
      if (index > -1) {
        selectedFeedbacks.value.splice(index, 1)
      } else {
        selectedFeedbacks.value.push(feedbackId)
      }
    }

    const toggleSelectAll = () => {
      if (selectedFeedbacks.value.length === filteredFeedbacks.value.length) {
        selectedFeedbacks.value = []
      } else {
        selectedFeedbacks.value = filteredFeedbacks.value.map(f => f.id)
      }
    }

    const deleteSelectedFeedbacks = () => {
      if (selectedFeedbacks.value.length === 0) return
      showDeleteModal.value = true
    }

    const cancelDelete = () => {
      showDeleteModal.value = false
      deletePassword.value = ''
    }

    const confirmDelete = async () => {
      if (!deletePassword.value) {
        alert('Please enter admin password.')
        return
      }

      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(deletePassword.value)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

        const expectedHash = 'a5f117dab01837a8abc8555e4587f344767375139f2a1af8429ff1af8be6ce4f'

        if (hashHex !== expectedHash) {
          alert('Incorrect password.')
          return
        }

        deleting.value = true

        for (const feedbackId of selectedFeedbacks.value) {
          await feedbackService.deleteFeedback(feedbackId)
        }

        feedbacks.value = feedbacks.value.filter(f => !selectedFeedbacks.value.includes(f.id))
        selectedFeedbacks.value = []
        filterFeedbacks()

        showDeleteModal.value = false
        deletePassword.value = ''
        alert('Feedbacks deleted successfully!')
      } catch (error) {
        console.error('Error deleting feedbacks:', error)
        alert('Error deleting feedbacks: ' + error.message)
      } finally {
        deleting.value = false
      }
    }

    const toggleAboutDropdown = () => {
      showAboutDropdown.value = !showAboutDropdown.value
      showFeedbackDropdown.value = false
      showDateDropdown.value = false
      aboutFilterSearch.value = ''
    }

    const toggleFeedbackDropdown = () => {
      showFeedbackDropdown.value = !showFeedbackDropdown.value
      showAboutDropdown.value = false
      showDateDropdown.value = false
      feedbackFilterSearch.value = ''
    }

    const toggleAboutFilter = (value) => {
      const idx = selectedAboutFilters.value.indexOf(value)
      if (idx > -1) {
        selectedAboutFilters.value.splice(idx, 1)
      } else {
        selectedAboutFilters.value.push(value)
      }
      filterFeedbacks()
    }

    const toggleFeedbackFilter = (value) => {
      const idx = selectedFeedbackFilters.value.indexOf(value)
      if (idx > -1) {
        selectedFeedbackFilters.value.splice(idx, 1)
      } else {
        selectedFeedbackFilters.value.push(value)
      }
      filterFeedbacks()
    }

    const selectAllAbout = () => {
      selectedAboutFilters.value = filteredAboutOptions.value.map(opt => opt.value)
      filterFeedbacks()
    }

    const clearAboutFilter = () => {
      selectedAboutFilters.value = []
      filterFeedbacks()
    }

    const selectAllFeedback = () => {
      selectedFeedbackFilters.value = [...filteredFeedbackOptions.value]
      filterFeedbacks()
    }

    const clearFeedbackFilter = () => {
      selectedFeedbackFilters.value = []
      filterFeedbacks()
    }

    const closeDropdowns = (e) => {
      if (!e.target.closest('.filter-dropdown') && !e.target.closest('.filter-icon-btn')) {
        showAboutDropdown.value = false
        showFeedbackDropdown.value = false
        showDateDropdown.value = false
      }
    }

    const exportFeedbacks = () => {
      const rows = filteredFeedbacks.value.map(f => ({
        About: f.about || '',
        Feedback: f.feedback || '',
        Email: f.email || '',
        Date: formatTimestamp(f.timestamp),
        Resolved: f.resolved ? 'Yes' : 'No'
      }))

      const headers = ['About', 'Feedback', 'Email', 'Date', 'Resolved']
      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          headers.map(h => {
            const val = String(row[h]).replace(/"/g, '""')
            return `"${val}"`
          }).join(',')
        )
      ].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `feedbacks_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    }

    const truncateText = (text, maxLength) => {
      if (!text) return '-'
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const formatAbout = (about) => {
      if (!about) return '-'
      if (about.toLowerCase() === 'cancel feedback') {
        return 'Subs. Cancel Feedback'
      }
      return about
    }

    const formatAboutLabel = (feedback) => {
      const parts = []
      if (feedback.about) parts.push(formatAbout(feedback.about))
      if (feedback.subscriptionCancelFeedback) parts.push('Subscription Cancel')
      if (feedback.isConvertedBack) parts.push('Converted Back')
      return parts.length ? parts.join(' · ') : '-'
    }

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-'

      // Handle Firestore timestamp objects
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000)
        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }

      // Handle regular Date objects or strings
      const date = new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const handleRowClick = (feedbackId, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/feedback/${feedbackId}`, '_blank')
      } else {
        router.push(`/feedback/${feedbackId}`)
      }
    }

    onMounted(() => {
      loadFeedbacks()
      document.addEventListener('click', closeDropdowns)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', closeDropdowns)
    })

    return {
      feedbacks,
      filteredFeedbacks,
      searchQuery,
      loading,
      resolveFilter,
      resolveOptions,
      resolvedCount,
      unresolvedCount,
      cancelCount,
      selectedFeedbacks,
      deleting,
      showDeleteModal,
      deletePassword,
      selectedAboutFilters,
      selectedFeedbackFilters,
      showAboutDropdown,
      showFeedbackDropdown,
      showDateDropdown,
      toggleDateDropdown,
      dateFrom,
      dateTo,
      clearDateFilter,
      aboutFilterSearch,
      feedbackFilterSearch,
      filteredAboutOptions,
      filteredFeedbackOptions,
      filterFeedbacks,
      updateResolved,
      toggleSelect,
      toggleSelectAll,
      deleteSelectedFeedbacks,
      cancelDelete,
      confirmDelete,
      toggleAboutDropdown,
      toggleFeedbackDropdown,
      toggleAboutFilter,
      toggleFeedbackFilter,
      selectAllAbout,
      clearAboutFilter,
      selectAllFeedback,
      clearFeedbackFilter,
      exportFeedbacks,
      truncateText,
      formatAbout,
      formatAboutLabel,
      formatTimestamp,
      handleRowClick
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }

.delete-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #ff4757, #ff6348);
  border: 1px solid #ff4757;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(255, 71, 87, 0.3);
}

.delete-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff3838, #ff5539);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
}

.delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.delete-btn svg {
  flex-shrink: 0;
}

.export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border: 1px solid #22c55e;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.export-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #16a34a, #15803d);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.4);
}

.export-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.export-btn svg {
  flex-shrink: 0;
}

.checkbox-cell {
  width: 50px;
  text-align: center;
}

.checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--primary-color);
}

.resolved-cell {
  width: 70px;
  text-align: center;
}

.resolved-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #22c55e;
}

.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.delete-modal {
  background: linear-gradient(135deg, rgba(26, 27, 46, 0.98), rgba(20, 21, 35, 0.98));
  padding: 32px;
  border-radius: 20px;
  max-width: 500px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.delete-modal h2 {
  margin: 0 0 16px 0;
  color: var(--text-primary);
  font-size: 24px;
}

.delete-modal .warning {
  color: #ff6b6b;
  margin-bottom: 20px;
  font-size: 14px;
}

.delete-modal input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 14px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.delete-modal input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn,
.delete-confirm-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.cancel-btn {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.delete-confirm-btn {
  background: linear-gradient(135deg, #ff4757, #ff6348);
  color: white;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
}

.delete-confirm-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff3838, #ff5539);
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
}

.delete-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-scroll-wrapper {
  overflow-x: auto;
}

.feedbacks-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.feedbacks-table th,
.feedbacks-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.feedbacks-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
}

.feedbacks-table td {
  color: var(--text-primary);
  font-size: 13px;
}

.feedback-text {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cancel-reason-tag {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  background: rgba(251, 146, 60, 0.12);
  color: #fb923c;
  white-space: nowrap;
}

.no-data {
  text-align: center;
  color: var(--text-secondary);
  padding: 32px !important;
}

.filterable-header {
  position: relative;
}

.header-filter-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-icon-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 3px 5px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  position: relative;
}

.filter-icon-btn:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.1);
}

.filter-icon-btn.has-filter {
  color: var(--primary-color);
  background: rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.3);
}

.filter-badge {
  font-size: 9px;
  font-weight: 700;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.filter-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  min-width: 240px;
  max-width: 360px;
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 10px;
  z-index: 100;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
}

.filter-dropdown-date {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.date-range-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-range-field label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}

.date-input {
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  color-scheme: dark;
}

.date-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.filter-search-input {
  width: 100%;
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 12px;
  margin-bottom: 8px;
  box-sizing: border-box;
}

.filter-search-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.filter-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.filter-actions button {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;
  font-weight: 500;
}

.filter-actions button:hover {
  text-decoration: underline;
}

.filter-options {
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.filter-options::-webkit-scrollbar {
  width: 4px;
}

.filter-options::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  transition: background 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.filter-option:hover {
  background: rgba(255, 255, 255, 0.06);
}

.filter-option-special {
  color: #fb923c;
  font-weight: 500;
}

.filter-option-special + .filter-option:not(.filter-option-special) {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 8px;
  margin-top: 4px;
}

.filter-option input[type="checkbox"] {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  accent-color: var(--primary-color);
  cursor: pointer;
}

.filter-option span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.filter-empty {
  padding: 12px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
}

.feedback-row {
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.feedback-row:hover {
  background: rgba(255, 255, 255, 0.05);
}
</style>
